import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { getFeaturedListingsProps } from '../../util/data';

import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import { H1 } from '../PageBuilder/Primitives/Heading';
import FallbackPage, { fallbackSections } from './FallbackPage';
import { ASSET_NAME } from './PrivacyPolicyPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);
const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

// This "content-only" component can be used in modals etc.
const PrivacyPolicyContent = props => {
  const { inProgress, error, data, featuredListings } = props;

  // We don't want to add h1 heading twice to the HTML (SEO issue).
  // Modal's header is mapped as h2
  const hasContent = data => typeof data?.content === 'string';
  const exposeContentAsChildren = data => {
    return hasContent(data) ? { children: data.content } : {};
  };

  if (!hasContent && inProgress) {
    return null;
  }

  const CustomHeading1 = props => <H1 as="h2" {...props} />;

  const isDefaultPlaceholder =
    data?.sections?.[0]?.blocks?.[0]?.text?.content?.includes('8410839') ||
    data?.sections?.[0]?.blocks?.[0]?.text?.content?.includes('Every marketplace business needs');

  const hasData = error === null && data && !isDefaultPlaceholder;
  const sectionsData = hasData ? data : fallbackSections;

  return (
    <SectionBuilder
      {...sectionsData}
      options={{
        featuredListings,
        fieldComponents: {
          heading1: { component: CustomHeading1, pickValidProps: exposeContentAsChildren },
        },
        isInsideContainer: true,
      }}
    />
  );
};

// Presentational component for PrivacyPolicyPage
const PrivacyPolicyPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;
  const rawPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  const isDefaultPlaceholder =
    rawPageData?.sections?.[0]?.blocks?.[0]?.text?.content?.includes('8410839') ||
    rawPageData?.sections?.[0]?.blocks?.[0]?.text?.content?.includes('Every marketplace business needs');

  // Strip duplicate H1 inside markdown block if section title already renders as an H1
  const sanitizedPageData = rawPageData
    ? {
        ...rawPageData,
        sections: rawPageData.sections?.map(s => {
          if (!s.title?.content) return s;
          return {
            ...s,
            blocks: s.blocks?.map(b => {
              if (b.text?.fieldType === 'markdown' && typeof b.text.content === 'string') {
                const titleStr = s.title.content.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const cleanContent = b.text.content.replace(
                  new RegExp(`^#\\s*${titleStr}\\s*\\n+`, 'i'),
                  ''
                );
                return { ...b, text: { ...b.text, content: cleanContent } };
              }
              return b;
            }),
          };
        }),
      }
    : null;

  return (
    <PageBuilder
      pageAssetsData={isDefaultPlaceholder ? null : (sanitizedPageData || rawPageData)}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
    />
  );
};

PrivacyPolicyPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const PrivacyPolicyPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(PrivacyPolicyPageComponent);

const PRIVACY_POLICY_ASSET_NAME = ASSET_NAME;
export { PRIVACY_POLICY_ASSET_NAME, PrivacyPolicyPageComponent, PrivacyPolicyContent };

export default PrivacyPolicyPage;
